<script setup lang="ts">
import type { FFDragEvent } from '@/types/Firefox/DragEvent'
import type { DeviceModel } from '@/types/models/DeviceModel'
import { IconItem, StackLayout, TypographyItem } from '@rfc/rfc-components-library'
import { EColors, EDeviceStatus } from '@rfc/rfc-components-library/types'
import type { Position } from '@vueuse/core'
import { ref } from 'vue'

type Props = { device?: DeviceModel }
defineProps<Props>()
type Emits = {
    (e: 'dragStart', device?: DeviceModel): void
    (e: 'dragEnd', position?: Position): void
}
const emit = defineEmits<Emits>()

const handle = ref<{ stackRef: HTMLDivElement } | null>(null)

const mouseDown = (event: any, device?: DeviceModel) => {
    event.stopPropagation()

    const hideDragImage = event.currentTarget?.cloneNode(true)
    hideDragImage.id = 'hidedraggeimage'
    const dragImage = event.currentTarget?.cloneNode(true)
    dragImage.id = 'draggeimage'

    dragImage.style.position = 'absolute'
    dragImage.style.visibility = 'hidden'
    dragImage.style.transform = 'translate(-50%, -50%)'
    dragImage.style['transition-timing-function'] = 'unset'
    dragImage.style['transition-duration'] = 'unset'
    hideDragImage.style.opacity = 0

    document.body.appendChild(hideDragImage)
    document.body.appendChild(dragImage)

    event.dataTransfer?.setDragImage(hideDragImage, event.x, event.y)
    event.dataTransfer.effectAllowed = 'move'

    emit('dragStart', device)
}
const mouseUp = (event: FFDragEvent) => {
    const dragImage = document.getElementById('draggeimage')
    const hideDragImage = document.getElementById('hidedraggeimage')
    if (dragImage && handle.value) {
        handle.value.stackRef.style.position = 'relative'
        handle.value.stackRef.style.removeProperty('visibility')
        handle.value.stackRef.style.removeProperty('left')
        handle.value.stackRef.style.removeProperty('top')
    }
    hideDragImage?.remove()
    dragImage?.remove()
    emit('dragEnd', { x: event._ffix_cx ?? event.x, y: event._ffix_cy ?? event.y })
}

const setPosition = (event: DragEvent) => {
    const dragImage = document.getElementById('draggeimage')

    if (dragImage && handle.value) {
        dragImage.style.removeProperty('visibility')
        dragImage.style.left = `${event.x}px`
        dragImage.style.top = `${event.y}px`
        dragImage.style.pointerEvents = 'none'
        handle.value.stackRef.style.visibility = 'hidden'
    }
}
</script>

<template>
    <StackLayout
        className="card-drag drag-card"
        :draggable="true"
        ref="handle"
        @dragstart="mouseDown($event, device)"
        @dragend="mouseUp"
        @drag="setPosition">
        <StackLayout class="inner space-between align-center px-8 py-2">
            <StackLayout direction="column">
                <TypographyItem
                    :label="device?.name"
                    class="section-text max-w-[200px] troncate inline-block" />
                <TypographyItem :label="`SN: ${device?.serial_number}`" class="" />
            </StackLayout>
            <IconItem
                v-if="device?.status === EDeviceStatus.ONLINE"
                icon="circle-slice-8"
                :type="EColors.SUCCESS" />
            <IconItem v-else icon="circle-slice-8" :type="EColors.ERROR" />
        </StackLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.device-drawer-section {
    background-color: var(--color-background-secondary);
    border-radius: 15px;
    box-shadow: 1px 1px 7px black;
}

.drag-card {
    max-width: 400px;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    transition-timing-function: ease-in-out;
    transition-duration: 0.2s;

    &:hover {
        box-shadow: 1px 1px 15px rgba(255, 124, 1, 0.75);
        transform: scale(1.08);
    }

    & .inner {
        z-index: 0;
        width: 100%;
        margin: 2px;
    }

    &::before {
        content: '';
        display: block;
        background: linear-gradient(90deg, $orange-dark 0%, $orange 50%, $orange-dark 100%);
        height: 300px;
        width: 50px;
        transform: translate(0);
        position: absolute;
        animation: rotate 4s linear forwards infinite;
        top: 50%;
        transform-origin: top center;
    }
}

.sort-icon {
    transition-timing-function: ease-in-out;
    transition-duration: 0.2s;
    &:hover {
        transform: scale(1.2) rotate(1deg);
    }
}
.card-drag {
    border-radius: 30px;
    min-height: 65px;
    & .inner {
        padding: 0.5rem 1rem;
        background: var(--color-background-secondary);
        color: #fff;
        border-radius: 30px;
        box-shadow: 1px 1px 7px rgb(163, 163, 163);
    }
}
.card {
    border-radius: 30px;
    box-shadow: 1px 1px 7px $grey-shadow;

    &:hover {
        box-shadow: 1px 1px 7px rgb(252, 127, 3);
        transition: ease-in-out 0.2s;
    }
    & .inner {
        background: var(--color-background-secondary);
        color: #fff;
        border-radius: 30px;
        box-shadow: 1px 1px 7px black;
    }
}

@keyframes rotate {
    from {
        transform: rotate(0);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
